import React, { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, cleanCpf, CpfInput, UserkeyInput, SystemMessage } from '../..';
import { useLogin } from '../../../hooks/useLogin';
import type { LoginFormProps } from '../../../types';
import { ROUTES } from '../../../routes/routesConfig';

import LoginSecondStep from './LoginSecondStepForm';

export const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  onLoadingChange,
  skipSecondStep,
}) => {
  const [showSecondStep, setShowSecondStep] = useState(false);
  const navigate = useNavigate();
  const [cpf, setCpf] = useState('');
  const [userkey, setUserkey] = useState('');
  const [isCpfValid, setIsCpfValid] = useState(false);
  const [isUserkeyValid, setIsUserkeyValid] = useState(false);
  const [secondStepData, setSecondStepData] = useState<{
    userId: number;
    email: boolean;
    phone: boolean;
    maskedEmail?: string | null;
    maskedCellphone?: string | null;
  } | null>(null);
  const mainContentId = useId();
  const formId = useId();
  const cpfInputId = useId();
  const userkeyInputId = useId();
  const errorMessageId = useId();

  const {
    isLoading,
    error,
    login,
    clearError,
    timeLeft,
    isCodeExpired,
    verificationCode,
    showCodeFields,
    sendCode,
    sendCodeToContact,
    fetchContacts,
    validateCode,
    resendCode,
    updateCode,
    resetVerificationState,
    resetState,
    contatos,
    vinculosRequestFailed,
    contatosRequestFailed,
    retryBusinessLinks,
    retryLoginContacts
  } = useLogin({
    onSuccess,
    skipSecondStep,
    onFormClear: () => {
      setCpf('');
      setUserkey('');
      setShowSecondStep(false);
      setSecondStepData(null);
    },
    onSecondStepRequired: (userId, email, phone, maskedEmail, maskedCellphone) => {
      if (skipSecondStep) {
        return;
      }
      setSecondStepData({
        userId,
        email,
        phone,
        maskedEmail,
        maskedCellphone
      });
      setShowSecondStep(true);
    }
  });

  // Notify parent about loading state changes
  // Only propagate from the first step. During the second step,
  // the child component (LoginSecondStep) is responsible for loading state.
  React.useEffect(() => {
    if (onLoadingChange && !showSecondStep) {
      onLoadingChange(isLoading);
    }
  }, [isLoading, onLoadingChange, showSecondStep]);

  // Update userkey validation when the value changes
  React.useEffect(() => {
    setIsUserkeyValid(userkey.length > 0);
  }, [userkey]);



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    clearError();

    // Call login through the hook - it handles everything
    await login(cleanCpf(cpf), userkey);
  };

  const handleCpfChange = (value: string) => {
    setCpf(value);
  };

  const handleCpfValidationChange = (isValid: boolean) => {
    setIsCpfValid(isValid);
  };

  const handleUserkeyChange = (value: string) => {
    setUserkey(value);
    // Update validation immediately when userkey changes
    setIsUserkeyValid(value.length > 0);
  };

  const handleUserkeyValidationChange = (_result: boolean | { isValid: boolean }) => {
    // For login, we consider it valid if the userkey is not empty
    const isValid = userkey.length > 0;
    setIsUserkeyValid(isValid);
  };


  const handleSecondStepError = (_error: string) => {
    // Error is already handled by the login hook
  };

  const handleBackToLogin = () => {
    // Reset second step state and return to login form
    setShowSecondStep(false);
    setSecondStepData(null);
    clearError();
  };

  const showRetryOnlyScreen = Boolean(error) && (vinculosRequestFailed || contatosRequestFailed);

  const handleRetryRequest = () => {
    if (vinculosRequestFailed) {
      void retryBusinessLinks();
      return;
    }
    void retryLoginContacts();
  };

  if (showSecondStep && secondStepData) {
    return (
      <LoginSecondStep
        onNext={onSuccess}
        onError={handleSecondStepError}
        onLoadingChange={onLoadingChange || (() => { })}
        onBack={handleBackToLogin}
        userId={secondStepData.userId}
        maskedEmail={secondStepData.maskedEmail}
        maskedCellphone={secondStepData.maskedCellphone}
        secondStepApi={{
          isLoading,
          error,
          timeLeft,
          isCodeExpired,
          verificationCode,
          showCodeFields,
          sendCode,
          sendCodeToContact,
          fetchContacts,
          validateCode,
          resendCode,
          updateCode,
          clearError,
          resetVerificationState,
          resetState,
          contatos,
          vinculosRequestFailed,
          contatosRequestFailed,
          retryBusinessLinks,
          retryLoginContacts
        }}
      />
    );
  }

  if (showRetryOnlyScreen) {
    return (
      <div
        id={mainContentId}
        className="bsds-width-100 bsds-flex flex-col bsds-justify-content-center bsds-items-center bsds-mt-48"
        role="main"
        aria-label="Repetir tentativa de login"
      >
        <Button
          type="button"
          color="primary"
          fill="outline"
          onClick={handleRetryRequest}
          disabled={isLoading}
          aria-label="Tentar carregar novamente"
        >
          Tente novamente
        </Button>
      </div>
    );
  }

  return (
    <div
      id={mainContentId}
      className="bsds-width-100 bsds-flex flex-col bsds-justify-content-center bsds-items-center bsds-justify-content-center bsds-mt-48"
      role="main"
      aria-label="Formulário de login"
    >
      <div className="bsds-text-start bsds-mb-24 bsds-width-100">
        <div className="bsds-display-flex bsds-align-items-center bsds-justify-content-start bsds-mb-8">
          <div className="w-[2.5px] h-10 bg-red-600 bsds-mr-12" aria-hidden="true" />
          <h2 className="bsds-mb-0 bsds-mt-0 leading-none" style={{ fontSize: '32px' }}>
            Bem-vindo ao <span className="font-bold">Portal</span>
          </h2>
        </div>
        <h6 className="bsds-text-sm" style={{ fontSize: '12px', fontWeight: 400 }}>
          Utilize seus dados para acessar o sistema.
        </h6>
      </div>

      <form
        id={formId}
        className="bsds-width-100"
        onSubmit={handleSubmit}
        aria-label="Formulário de login"
        noValidate
      >
        <div className="bsds-mb-16 bsds-mt-32">
          <div>
            <CpfInput
              value={cpf}
              onChange={handleCpfChange}
              onValidationChange={handleCpfValidationChange}
              required
              placeholder="123.456.789-00"
              label="Insira seu CPF"
              aria-label="CPF de acesso (obrigatório)"
              aria-describedby={`${cpfInputId}-help`}
              aria-required
            />
            <span
              id={`${cpfInputId}-help`}
              className="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0"
              style={{ clip: 'rect(0, 0, 0, 0)', clipPath: 'inset(50%)' }}
            >
              Digite seu CPF para fazer login
            </span>
          </div>

          <div className="bsds-mt-24">
            <UserkeyInput
              value={userkey}
              onChange={handleUserkeyChange}
              onValidationChange={handleUserkeyValidationChange}
              placeholder="Senha"
              required
              showValidationRules={false}
              label="Insira sua senha"
              aria-label="Senha para logar (obrigatório)"
              aria-describedby={`${userkeyInputId}-help`}
              aria-required
            />
            <span
              id={`${userkeyInputId}-help`}
              className="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0"
              style={{ clip: 'rect(0, 0, 0, 0)', clipPath: 'inset(50%)' }}
            >
              Digite sua senha para fazer login
            </span>
          </div>
        </div>

        <div className="bsds-display-flex bsds-justify-content-end bsds-align-items-center">
          <button
            type="button"
            className="bsds-text-sm bsds-text-primary cursor-pointer underline bg-transparent border-none p-0"
            onClick={() => navigate(ROUTES.FORGOT_USERKEY)}
            aria-label="Esqueci minha senha - recuperar acesso"
          >
            Esqueci a senha
          </button>
        </div>

        <div className="bsds-mt-40">
          <Button
            color="primary"
            type="submit"
            fill="solid"
            disabled={
              isLoading || // Disabled during loading
              !cpf || // Disabled if CPF is empty
              !isCpfValid || // Disabled if CPF is not valid
              !userkey || // Disabled if userkey is empty
              !isUserkeyValid // Disabled if userkey is not valid
            }
            aria-label={
              isLoading
                ? 'Fazendo login...'
                : !cpf || !isCpfValid || !userkey || !isUserkeyValid
                  ? 'Preencha o CPF e a senha corretamente para fazer login'
                  : 'Fazer login na conta'
            }
            aria-busy={isLoading}
          >
            {'Entrar'}
          </Button>
          {error && (
            <div
              id={errorMessageId}
              className="bsds-mt-4"
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
            >
              <SystemMessage
                message={error}
                type="error"
              />
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
